import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles, featuredVideos, hotVideos, categoryVideos } from '@/mocks/videos';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

interface VideoArticle {
  id: number;
  title: string;
  category: string;
  views: number;
  rating: number;
  duration: string;
  thumbnail: string;
  tags?: string[];
  year?: number;
  region?: string;
  episodes?: number;
  actors?: string[];
  director?: string;
  host?: string;
  studio?: string;
  duration_min?: number;
}

interface TextArticle {
  id: string;
  title: string;
  category: string;
  summary: string;
  content: string;
  publishDate: string;
  author: string;
  views: number;
  tags: string[];
  thumbnail: string;
}

const categoryNames: Record<string, string> = {
  dianshi: '电视剧',
  dianying: '电影',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  duanju: '短剧',
  tiyu: '体育',
  yinyue: '音乐',
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const textArticle = articles.find((a) => a.id === id) as TextArticle | undefined;

  const allVideos: VideoArticle[] = [
    ...featuredVideos,
    ...hotVideos,
    ...Object.values(categoryVideos).flat(),
  ];
  const uniqueVideos = allVideos.filter(
    (v, i, arr) => arr.findIndex((x) => x.id === v.id) === i
  );
  const video = uniqueVideos.find((v) => String(v.id) === id);

  if (textArticle) {
    const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: textArticle.title,
      description: textArticle.summary,
      author: { '@type': 'Person', name: textArticle.author },
      datePublished: textArticle.publishDate,
      image: textArticle.thumbnail,
      publisher: { '@type': 'Organization', name: '91tv' },
    };

    return (
      <>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-[900px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb />
            <article>
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full">
                    {categoryNames[textArticle.category] || textArticle.category}
                  </span>
                  {textArticle.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-background-100 text-foreground-500 px-2 py-0.5 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading leading-tight">
                  {textArticle.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                  <span>{textArticle.author}</span>
                  <span>{textArticle.publishDate}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {textArticle.views >= 10000 ? `${(textArticle.views / 10000).toFixed(0)}万阅读` : `${textArticle.views}阅读`}
                  </span>
                </div>
              </header>

              <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
                <img
                  src={textArticle.thumbnail}
                  alt={textArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: textArticle.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {textArticle.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h3 className="text-lg font-semibold text-foreground-900 mb-5 font-heading">相关文章</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.thumbnail}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (video) {
    const relatedVideos = uniqueVideos.filter(
      (v) => v.id !== video.id && v.category === video.category
    ).slice(0, 4);

    const videoSchema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: video.title,
      description: video.title,
      thumbnailUrl: video.thumbnail,
      duration: video.duration,
      uploadDate: '2024-01-01',
    };

    return (
      <>
        <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb />
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1">
                <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 cursor-pointer transition-colors">
                      <i className="ri-play-fill text-white text-3xl md:text-4xl ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-sm px-3 py-1 rounded-md">
                    {video.duration}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    {video.tags && video.tags.map((tag: string) => (
                      <span key={tag} className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                    <span className="text-xs bg-background-100 text-foreground-500 px-2 py-0.5 rounded-full">
                      {categoryNames[video.category]}
                    </span>
                  </div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                    {video.title}
                  </h1>
                  <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {video.views >= 10000 ? `${(video.views / 10000).toFixed(0)}万次播放` : `${video.views}次播放`}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400"></i>
                      {video.rating}
                    </span>
                  </div>
                </div>

                <div className="mt-8 p-5 bg-background-100/70 rounded-xl border border-background-200/70">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-3">影片信息</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {video.year && (
                      <div>
                        <span className="text-foreground-500">年份：</span>
                        <span className="text-foreground-800">{video.year}</span>
                      </div>
                    )}
                    {video.region && (
                      <div>
                        <span className="text-foreground-500">地区：</span>
                        <span className="text-foreground-800">{video.region}</span>
                      </div>
                    )}
                    {video.episodes && (
                      <div>
                        <span className="text-foreground-500">集数：</span>
                        <span className="text-foreground-800">{video.episodes}集</span>
                      </div>
                    )}
                    {video.duration_min && (
                      <div>
                        <span className="text-foreground-500">片长：</span>
                        <span className="text-foreground-800">{video.duration_min}分钟</span>
                      </div>
                    )}
                    {video.director && (
                      <div>
                        <span className="text-foreground-500">导演：</span>
                        <span className="text-foreground-800">{video.director}</span>
                      </div>
                    )}
                    {video.host && (
                      <div>
                        <span className="text-foreground-500">主持人：</span>
                        <span className="text-foreground-800">{video.host}</span>
                      </div>
                    )}
                    {video.studio && (
                      <div>
                        <span className="text-foreground-500">制作：</span>
                        <span className="text-foreground-800">{video.studio}</span>
                      </div>
                    )}
                    {video.actors && (
                      <div className="col-span-2">
                        <span className="text-foreground-500">主演：</span>
                        <span className="text-foreground-800">{video.actors.join(' / ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="lg:w-[340px] flex-shrink-0">
                <h3 className="text-sm font-semibold text-foreground-800 mb-4">相关推荐</h3>
                <div className="space-y-3">
                  {relatedVideos.map((rv) => (
                    <Link
                      key={rv.id}
                      to={`/article/${rv.id}`}
                      className="flex gap-3 group cursor-pointer"
                    >
                      <div className="relative flex-shrink-0 w-40 h-24 rounded-lg overflow-hidden">
                        <img
                          src={rv.thumbnail}
                          alt={rv.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                          {rv.duration}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {rv.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                          <span className="flex items-center gap-0.5">
                            <i className="ri-star-fill text-amber-400 text-xs"></i>
                            {rv.rating}
                          </span>
                          <span>
                            {rv.views >= 10000 ? `${(rv.views / 10000).toFixed(0)}万` : rv.views}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
          <p className="mt-4 text-foreground-600">内容未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
        </div>
      </div>
      <Footer />
    </>
  );
}